'use client';

import { useEffect, useRef, useState } from 'react';
import { Phone, Check } from 'lucide-react';
import { PHONE_DIALER_COPY } from '@/lib/simple-setup/customer-wins';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;
const SEQUENCE = '**21*5551234567#';
const PER_CHAR_MS = 90;
const HOLD_AFTER_DIAL_MS = 600;
const SUCCESS_HOLD_MS = 2400;
const RESET_MS = 600;

/**
 * V4 cyan-pillar phone dialer mockup.
 * The **21*5551234567# sequence types itself out, lights up the call
 * button, then shows a green forwarding-active overlay. Loops.
 *
 * Behavior preserved verbatim from the legacy version — only the
 * legacy deprecated token namespace and class names were rewritten
 * onto the V4 namespace (--cyan*, --jade*, --paper, --hair-2 +
 * .v4-sp-* classes).
 */
export function PhoneDialerMockup() {
  const [typed, setTyped] = useState('');
  const [success, setSuccess] = useState(false);
  const [pressed, setPressed] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cycleRef = useRef<number | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setTyped(SEQUENCE);
      setSuccess(true);
      return;
    }

    const timers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    const runCycle = () => {
      if (cancelled) return;
      setTyped('');
      setSuccess(false);
      setPressed(null);

      let elapsed = 0;
      [...SEQUENCE].forEach((ch, i) => {
        const t = setTimeout(() => {
          if (cancelled) return;
          setTyped((prev) => prev + ch);
          setPressed(ch);
          const release = setTimeout(() => setPressed(null), 130);
          timers.push(release);
        }, elapsed);
        timers.push(t);
        elapsed += PER_CHAR_MS;
        if (i === 1 || i === 4) elapsed += 100;
      });

      const successAt = elapsed + HOLD_AFTER_DIAL_MS;
      const showT = setTimeout(() => {
        if (cancelled) return;
        setSuccess(true);
      }, successAt);
      timers.push(showT);

      const restartT = setTimeout(() => {
        if (cancelled) return;
        runCycle();
      }, successAt + SUCCESS_HOLD_MS + RESET_MS);
      timers.push(restartT);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && cycleRef.current === null) {
            cycleRef.current = 1;
            runCycle();
          }
        }
      },
      { threshold: 0.35 }
    );
    obs.observe(node);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      obs.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div className="v4-sp-phone-glow" aria-hidden="true" />
      <div
        className="v4-sp-phone-shell"
        role="img"
        aria-label={PHONE_DIALER_COPY.ariaLabel}
      >
        <div
          className="flex items-center justify-between px-5 pt-4"
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-geist-mono)',
            color: 'var(--dim)',
            letterSpacing: '0.04em',
          }}
        >
          <span>9:41</span>
          <div className="flex items-center gap-1">
            <span style={{ width: 4, height: 4, borderRadius: 9999, background: 'rgba(244,244,240,0.55)' }} />
            <span style={{ width: 4, height: 4, borderRadius: 9999, background: 'rgba(244,244,240,0.55)' }} />
            <span style={{ width: 4, height: 4, borderRadius: 9999, background: 'rgba(244,244,240,0.55)' }} />
            <span
              style={{
                marginLeft: 4,
                display: 'inline-block',
                width: 20,
                height: 10,
                borderRadius: 2,
                border: '1px solid rgba(244,244,240,0.45)',
                position: 'relative',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  inset: 2,
                  right: 2,
                  background: 'rgba(244,244,240,0.65)',
                  borderRadius: 1,
                }}
              />
            </span>
          </div>
        </div>

        <div className="px-5 pt-5 pb-4">
          <div
            style={{
              fontSize: 10,
              fontFamily: 'var(--font-geist-mono)',
              textTransform: 'uppercase',
              letterSpacing: '0.20em',
              color: 'var(--dim)',
            }}
          >
            {PHONE_DIALER_COPY.keypadLabel}
          </div>
          <div
            className="v4-sp-phone-display"
            style={{
              marginTop: 8,
              fontSize: 26,
              color: 'var(--paper)',
              minHeight: 34,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            aria-live="polite"
          >
            {typed}
            <span
              style={{
                color: 'var(--cyan-glow)',
                animation: 'v4-blip 1s ease-in-out infinite',
              }}
            >
              {typed.length < SEQUENCE.length ? '|' : ''}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 px-6 pb-4">
          {KEYS.map((k) => (
            <div
              key={k}
              className={`v4-sp-keypad-key${pressed === k ? ' is-pressed' : ''}`}
              aria-hidden="true"
            >
              {k}
            </div>
          ))}
        </div>

        <div className="px-6 pb-6">
          <div
            style={{
              height: 48,
              borderRadius: 9999,
              background: 'rgba(52, 211, 153, 0.12)',
              border: '1px solid rgba(52, 211, 153, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              color: 'var(--jade)',
            }}
          >
            <Phone className="h-4 w-4" />
            <span
              style={{
                fontSize: 12,
                fontFamily: 'var(--font-geist-mono)',
                textTransform: 'uppercase',
                letterSpacing: '0.20em',
              }}
            >
              {PHONE_DIALER_COPY.callLabel}
            </span>
          </div>
        </div>

        <div className="v4-sp-success-veil" data-show={success}>
          <div className="v4-sp-jade-orb">
            <Check className="h-7 w-7" style={{ color: '#fff' }} strokeWidth={3} />
          </div>
          <div
            style={{
              fontFamily: 'var(--font-geist-sans)',
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--paper)',
            }}
          >
            {PHONE_DIALER_COPY.successTitle}
          </div>
          <div
            style={{
              fontSize: 12,
              fontFamily: 'var(--font-geist-mono)',
              textTransform: 'uppercase',
              letterSpacing: '0.20em',
              color: 'var(--jade)',
            }}
          >
            {PHONE_DIALER_COPY.successSub}
          </div>
        </div>
      </div>
    </div>
  );
}
